import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

dayjs.extend(duration);
dayjs.extend(relativeTime);

/** Ag-Grid doesn't re-render the header when hooks change, so we need to refresh the header manually. */
export const TimePassed = (p: {
  className?: string;
  time: string | number;
  onRefresh?: () => void;
  username?: string | null;
  /** Use short format (s, m, h, d, w) for column headers */
  shortFormat?: boolean;
}) => {
  const [timePassedLabel, setTimePassedLabel] = useState<string | null>(getTimePassedLabel(p.time, p.shortFormat));
  const onRefresh = p.onRefresh;

  useEffect(() => {
    const interval = setInterval(() => {
      const passedTime = getTimePassedLabel(p.time, p.shortFormat);
      if (passedTime !== timePassedLabel) {
        onRefresh?.();
        setTimePassedLabel(passedTime);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [p.time, onRefresh, timePassedLabel, p.shortFormat]);

  if (!timePassedLabel) return null;

  return (
    <span className={p.className}>
      {p.username && (
        <span className="font-semibold" data-testid="status-header-username">
          {p.username.toUpperCase()}:{" "}
        </span>
      )}
      {timePassedLabel}
    </span>
  );
};

const getTimePassedLabel = (time: string | number, shortFormat?: boolean) => {
  const editedAt = dayjs(time);
  if (!editedAt.isValid()) return null;

  if (shortFormat) {
    // Short format for column headers: match old webapp (s, m, h, d, w - stop at weeks)
    const now = dayjs();
    const diffInSeconds = now.diff(editedAt, "second");

    if (diffInSeconds < 60) {
      const seconds = diffInSeconds;
      return seconds <= 1 ? "1s" : `${seconds}s`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d`;
    } else {
      const weeks = Math.floor(diffInSeconds / 604800);
      return `${weeks}w`;
    }
  } else {
    // Long format for tooltips and other uses
    const timePassed = dayjs.duration(dayjs().diff(editedAt));
    const humanized =
      timePassed
        .humanize()
        ?.replace(/^a (?!few)/, "1 ")
        .replace(/^an /, "1 ") || null;
    return abbreviateTime(humanized);
  }
};

function abbreviateTime(timeString: string | null): string | null {
  if (!timeString) return null;

  return timeString
    .replace(/seconds?/g, (match) => (match === "second" ? "sec" : "secs"))
    .replace(/minutes?/g, (match) => (match === "minute" ? "min" : "mins"))
    .replace(/hours?/g, (match) => (match === "hour" ? "hr" : "hrs"))
    .replace(/days?/g, (match) => (match === "day" ? "day" : "days"))
    .replace(/weeks?/g, (match) => (match === "week" ? "wk" : "wks"))
    .replace(/months?/g, (match) => (match === "month" ? "mth" : "mths"))
    .replace(/years?/g, (match) => (match === "year" ? "yr" : "yrs"));
}
